<template>
  <div class="design-hub">
    <aside class="design-sidebar" :class="{ collapsed: sidebarCollapsed && isCompact }">
      <div class="sidebar-head">
        <h2>产品设计文档</h2>
        <el-button
          v-if="isCompact"
          class="toggle-btn"
          text
          @click="sidebarCollapsed = !sidebarCollapsed"
        >
          {{ sidebarCollapsed ? '目录' : '收起' }}
        </el-button>
      </div>
      <p class="sidebar-hint">仅管理员可见。源文件在仓库根目录；修改后请运行 <code>scripts/sync-design-docs.sh</code> 同步。</p>
      <ul class="doc-list">
        <li
          v-for="doc in docs"
          :key="doc.id"
          :class="{ active: doc.id === activeId }"
          @click="selectDoc(doc.id)"
        >
          <span class="doc-title">{{ doc.title }}</span>
          <span class="doc-desc">{{ doc.description }}</span>
        </li>
      </ul>
      <el-button
        v-if="activeDoc"
        class="open-raw"
        link
        type="primary"
        :disabled="!frameSrc"
        @click="openInNewWindow"
      >
        新窗口打开当前文档
      </el-button>
    </aside>

    <main class="design-main">
      <div v-if="loading" class="design-state">加载中…</div>
      <div v-else-if="error" class="design-state design-state--error">{{ error }}</div>
      <iframe
        v-else-if="frameSrc"
        :key="frameSrc"
        class="design-frame"
        :src="frameSrc"
        title="产品设计文档"
      />
    </main>
  </div>
</template>

<script>
import { DESIGN_DOCS, designDocApiUrl, findDesignDoc } from '../config/designDocs.js'

const COMPACT_BREAKPOINT = 768

export default {
  name: 'DesignHubView',
  data() {
    return {
      docs: DESIGN_DOCS,
      activeId: 'product',
      sidebarCollapsed: false,
      isCompact: false,
      loading: false,
      error: '',
      frameSrc: '',
      blobUrl: ''
    }
  },
  computed: {
    activeDoc() {
      return findDesignDoc(this.activeId)
    }
  },
  watch: {
    '$route.params.docId': {
      immediate: true,
      handler(id) {
        const doc = DESIGN_DOCS.find((d) => d.id === id)
        this.activeId = doc ? doc.id : 'product'
        this.loadActiveDoc()
      }
    }
  },
  mounted() {
    this.checkCompact()
    window.addEventListener('resize', this.checkCompact)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkCompact)
    this.revokeBlob()
  },
  methods: {
    checkCompact() {
      this.isCompact = window.innerWidth < COMPACT_BREAKPOINT
    },
    revokeBlob() {
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
        this.blobUrl = ''
      }
    },
    async loadActiveDoc() {
      const doc = this.activeDoc
      if (!doc?.file) {
        this.frameSrc = ''
        return
      }
      const token = localStorage.getItem('jwt_token')
      if (!token) {
        this.error = '请先登录管理员账号'
        this.frameSrc = ''
        return
      }
      this.loading = true
      this.error = ''
      this.revokeBlob()
      this.frameSrc = ''
      try {
        const res = await fetch(designDocApiUrl(doc.file), {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.status === 401 || res.status === 403) {
          throw new Error('无权查看产品设计文档')
        }
        if (!res.ok) {
          throw new Error('文档加载失败')
        }
        const blob = await res.blob()
        this.blobUrl = URL.createObjectURL(blob)
        this.frameSrc = this.blobUrl
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    openInNewWindow() {
      if (this.frameSrc) {
        window.open(this.frameSrc, '_blank', 'noopener')
      }
    },
    selectDoc(id) {
      this.activeId = id
      const path = id === 'product' ? '/admin/design' : `/admin/design/${id}`
      if (this.$route.path !== path) {
        this.$router.push(path)
      } else {
        this.loadActiveDoc()
      }
      if (this.isCompact) {
        this.sidebarCollapsed = true
      }
    }
  }
}
</script>

<style scoped>
.design-hub {
  display: flex;
  min-height: calc(100vh - 120px);
  background: #f0f2f5;
}
.design-sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e4e7ec;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.design-sidebar.collapsed {
  display: none;
}
.sidebar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.sidebar-head h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #35946a;
}
.sidebar-hint {
  font-size: 12px;
  color: #909399;
  margin: 8px 0 16px;
  line-height: 1.5;
}
.sidebar-hint code {
  font-size: 11px;
  background: #f4f4f5;
  padding: 1px 4px;
  border-radius: 3px;
}
.doc-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}
.doc-list li {
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s;
}
.doc-list li:hover {
  background: #f5f7fa;
}
.doc-list li.active {
  background: #eef9f3;
  border-color: #42b983;
}
.doc-title {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}
.doc-desc {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
.open-raw {
  margin-top: 12px;
  align-self: flex-start;
}
.design-main {
  flex: 1;
  min-width: 0;
  padding: 0;
}
.design-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 120px);
  color: #909399;
}
.design-state--error {
  color: #f56c6c;
}
.design-frame {
  width: 100%;
  height: calc(100vh - 120px);
  border: none;
  background: #fff;
}
@media (max-width: 768px) {
  .design-hub {
    flex-direction: column;
  }
  .design-sidebar {
    width: 100%;
    max-height: 40vh;
  }
  .design-frame,
  .design-state {
    height: calc(100vh - 200px);
  }
}
</style>
